// Mount toolkit quick-bar pills into the admin Menu dock row.
// React re-renders of AdminHamburgerNav wipe imperative DOM children — this hook re-attaches.

import { useEffect, useState } from 'react';

const ROW_SELECTOR = '.admin-hamburger-container .admin-fab-row';
const SLOT_CLASS = 'admin-fab-toolkit-slot';

/**
 * @param {string} dockClassName — e.g. packers-pill-dock
 * @returns {HTMLElement | null} portal target for createPortal
 */
export function useAdminFabDockMount(dockClassName) {
    const [dockNode, setDockNode] = useState(null);

    useEffect(() => {
        if (!dockClassName) return undefined;

        let portalNode = null;
        let pollTimer = null;
        let observer = null;

        const ensureDock = () => {
            const row = document.querySelector(ROW_SELECTOR);
            if (!row) return false;

            let slot = row.querySelector(`.${SLOT_CLASS}`);
            if (!slot) {
                slot = document.createElement('div');
                slot.className = SLOT_CLASS;
                row.insertBefore(slot, row.firstChild);
            }

            let node = slot.querySelector(`.${dockClassName}`);
            if (!node) {
                node = document.createElement('div');
                node.className = dockClassName;
                slot.appendChild(node);
            }

            portalNode = node;
            setDockNode(node);
            return true;
        };

        const watchRow = () => {
            const row = document.querySelector(ROW_SELECTOR);
            if (!row) return;

            observer = new MutationObserver(() => {
                const slot = row.querySelector(`.${SLOT_CLASS}`);
                const mounted = slot?.querySelector(`.${dockClassName}`);
                if (!mounted) {
                    ensureDock();
                }
            });

            observer.observe(row, { childList: true, subtree: true });
        };

        if (!ensureDock()) {
            pollTimer = window.setInterval(() => {
                if (ensureDock()) {
                    window.clearInterval(pollTimer);
                    pollTimer = null;
                    watchRow();
                }
            }, 200);
        } else {
            watchRow();
        }

        return () => {
            if (pollTimer) window.clearInterval(pollTimer);
            observer?.disconnect();
            if (portalNode?.parentNode) {
                portalNode.parentNode.removeChild(portalNode);
            }
            setDockNode(null);
        };
    }, [dockClassName]);

    return dockNode;
}
